import React, { useState, useEffect, useRef } from 'react';
import { useReactive } from 'ahooks';
import { Avatar, Progress, Button, InputNumber, message } from 'kenshin';
import TablePro from '@/components/TablePro';
import ModalFormPro from '@/components/ModalFormPro';
import { getTokenConfigPage, initQuota } from './_service';
import './index.less';

// 状态枚举
const STATUS_MAP = {
  normal: { label: '正常', color: '#52c41a' },
  warning: { label: '预警', color: '#faad14' },
  danger: { label: '超额', color: '#ff4d4f' },
};

// 根据使用率判断状态
function getStatus(usageRate) {
  if (usageRate >= 100) return 'danger';
  if (usageRate >= 80) return 'warning';
  return 'normal';
}

// 格式化数字为万单位
function formatQuota(num) {
  if (!num && num !== 0) return '-';
  if (num >= 10000) {
    return `${(num / 10000).toFixed(2)}万`;
  }
  return num.toLocaleString();
}

const TokenQuotaManage = () => {
  const tableRef = useRef();
  const modalRef = useRef();
  const [modalVisible, setModalVisible] = useState(false);
  const [currentRow, setCurrentRow] = useState(null);
  const [saving, setSaving] = useState(false);
  const [addQuotaValue, setAddQuotaValue] = useState(0);

  const state = useReactive({
    loading: false,
    dataSource: [],
    pagination: {
      current: 1,
      pageSize: 10,
      total: 0,
    },
  });

  // 加载列表
  const loadData = async (pageNo = 1, pageSize = 10) => {
    state.loading = true;
    try {
      const res = await getTokenConfigPage({
        page: {
          pageNo,
          pageSize,
          sortFields: [{ name: 'usedQuota', value: 1 }], // 按使用量倒序
        },
      });

      if (res?.success && res?.data) {
        state.dataSource = res.data.datas || [];
        state.pagination = {
          current: res.data.pageNo || pageNo,
          pageSize: res.data.pageSize || pageSize,
          total: res.data.totalCount || 0,
        };
      } else {
        message.error(res?.errorMessage || '加载失败');
      }
    } catch (err) {
      console.error('加载列表失败:', err);
      message.error('加载失败');
    } finally {
      state.loading = false;
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  // 打开增加额度弹窗
  const handleAddQuota = (row) => {
    setCurrentRow(row);
    setAddQuotaValue(0);
    setModalVisible(true);
  };

  // 保存配额
  const handleSave = async (values) => {
    setSaving(true);
    try {
      // 新总额度 = 当前额度 + 要增加的额度
      const newTotalQuota =
        (+currentRow?.totalQuota || 0) + (+values.addQuota || 0);

      const res = await initQuota({
        id: currentRow.id,
        empId: currentRow.empId,
        empName: currentRow.empName,
        totalQuota: newTotalQuota,
      });

      if (res?.success) {
        message.success('设置成功');
        setModalVisible(false);
        loadData(state.pagination.current, state.pagination.pageSize);
      } else {
        message.error(res?.errorMessage || '设置失败');
      }
    } catch (err) {
      console.error('设置配额失败:', err);
      message.error('设置失败');
    } finally {
      setSaving(false);
    }
  };

  // 弹窗表单配置
  const edtaFormItem = [
    {
      name: 'empName',
      label: '员工',
      type: 'Input',
      props: {
        disabled: true,
      },
    },
    {
      name: 'currentQuota',
      label: '当前额度',
      render: () => <span>{formatQuota(currentRow?.totalQuota)}</span>,
    },
    {
      name: 'addQuota',
      label: '增加额度',
      type: 'InputNumber',
      props: {
        style: { width: '100%' },
        placeholder: '请输入要增加的额度',
        onChange: (val) => setAddQuotaValue(val || 0),
      },
      required: '请输入要增加的额度',
    },
    {
      name: 'newTotalQuota',
      label: '新总额度',
      render: () => {
        const currentQuota = +currentRow?.totalQuota || 0;
        const newTotal = +currentQuota + addQuotaValue;
        return (
          <span style={{ fontWeight: 500, color: '#1890ff' }}>{newTotal}</span>
        );
      },
    },
  ];

  // 表格列
  const columns = [
    {
      title: '姓名',
      dataIndex: 'empName',
      width: 200,
      render: (_, row) => {
        const deptNames =
          row.belongDeptList?.map((d) => d.value).join('、') || '-';
        return (
          <div className="empCell">
            <Avatar
              src={row.empHeadImg}
              size={40}
              style={{ backgroundColor: '#87d068' }}
            >
              {row.empName?.charAt(0) || 'U'}
            </Avatar>
            <div className="empInfo">
              <div className="empName">{row.empName || '-'}</div>
              <div className="empDept">{deptNames}</div>
            </div>
          </div>
        );
      },
    },
    {
      title: '总额度',
      dataIndex: 'totalQuota',
      width: 120,
      align: 'center',
      render: (val) => formatQuota(val),
    },
    {
      title: '剩余额度',
      dataIndex: 'remainQuota',
      width: 150,
      render: (_, row) => {
        const total = row.totalQuota || 0;
        const used = row.usedQuota || 0;
        const remain = total - used;
        const remainPercent =
          total > 0 ? Math.max((remain / total) * 100, 0) : 0;
        const isLow = remainPercent < 20;
        const isZero = remain <= 0;

        return (
          <div className="remainCell">
            <Progress
              percentage={remainPercent}
              strokeWidth={6}
              showText={false}
            />
            <span
              style={{
                color: isZero ? '#ff4d4f' : isLow ? '#faad14' : '#52c41a',
              }}
            >
              {formatQuota(Math.max(remain, 0))}
            </span>
          </div>
        );
      },
    },
    {
      title: '使用率',
      dataIndex: 'usageRate',
      width: 180,
      render: (_, row) => {
        const total = row.totalQuota || 0;
        const used = row.usedQuota || 0;
        const rate = total > 0 ? Math.min((used / total) * 100, 100) : 0;
        const status = getStatus(rate);
        const statusInfo = STATUS_MAP[status];

        return (
          <div className="usageCell">
            <span className="statusTag" style={{ color: statusInfo.color }}>
              {statusInfo.label}
            </span>
          </div>
        );
      },
    },
    {
      title: '操作',
      key: 'action',
      width: 100,
      fixed: 'right',
      render: (_, row) => (
        <Button type="text" onClick={() => handleAddQuota(row)}>
          增加额度
        </Button>
      ),
    },
  ];

  return (
    <div className="token-quota-manage">
      <div className="pageHeader">
        <h2>AI额度管理</h2>
        <p className="pageDesc">管理员工AI创作Token配额，监控使用情况</p>
      </div>

      <TablePro
        rowKey="id"
        columns={columns}
        data={{
          datas: state.dataSource,
          totalCount: state.pagination.total,
          pageSize: state.pagination.pageSize,
        }}
        loading={state.loading}
        curColumns={true}
        scrollX={true}
        columnsOptions={false}
        onChange={(pagination) => {
          loadData(pagination.current, pagination.pageSize);
        }}
      />

      {/* 增加额度弹窗 */}
      <ModalFormPro
        title="增加额度"
        visible={modalVisible}
        onCancel={() => setModalVisible(false)}
        onSave={handleSave}
        edtaFormItem={edtaFormItem}
        formData={{
          empName: currentRow?.empName,
          addQuota: 0,
        }}
        btnLoading={saving}
        formProps={{
          labelCol: { span: 6 },
          wrapperCol: { span: 16 },
        }}
        width={400}
      />
    </div>
  );
};

export default TokenQuotaManage;
